// "Log a meal": camera-first photo of anything you cooked; optional recipe + caption.
import React, { useEffect, useRef, useState } from 'react';
import { Platform, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useNav, type RootParams } from '../navigation';
import { useDiet, useToday } from '../hooks';
import { useProgress } from '../store/progress';
import { useLibrary, useRecipeLookup } from '../store/library';
import { CONTENT_RECIPES } from '../../content';
import { pickPhoto, savePhoto, takePhoto, type PickedPhoto } from '../platform/media';
import Photo from '../components/Photo';
import { notify } from '../components/Dialogs';
import { Body, Button, Caption, Chip, Field, Row, Screen } from '../components/ui';
import { radius, spacing } from '../theme';

export default function LogMealScreen({ route }: NativeStackScreenProps<RootParams, 'LogMeal'>) {
  const { t } = useTranslation();
  const nav = useNav();
  const today = useToday();
  const logMeal = useProgress(s => s.logMeal);
  const setCookStep = useProgress(s => s.setCookStep);
  const lookup = useRecipeLookup();
  const ownRecipes = useLibrary(s => s.recipes);
  const diet = useDiet();
  const [photo, setPhoto] = useState<PickedPhoto | null>(null);
  const [caption, setCaption] = useState('');
  const [recipeId, setRecipeId] = useState<string | undefined>(route.params?.recipeId);
  const [saving, setSaving] = useState(false);
  const autoOpened = useRef(false);

  // Camera-first on phones (browsers need a tap before opening the camera).
  useEffect(() => {
    if (autoOpened.current || Platform.OS === 'web') return;
    autoOpened.current = true;
    takePhoto().then(ph => ph && setPhoto(ph)).catch(() => {});
  }, []);

  const recent = Object.values(ownRecipes).filter(r => !diet.hidden(r)).slice(0, 6).map(r => r.id);
  const suggestions = [...new Set([...(recipeId ? [recipeId] : []), ...recent, ...Object.keys(CONTENT_RECIPES).filter(id => !diet.hidden(CONTENT_RECIPES[id])).slice(0, 6)])];

  const save = async () => {
    if (!photo) return notify(t('logMeal.needPhoto'));
    setSaving(true);
    try {
      const ref = await savePhoto(photo);
      logMeal({ day: today, photo: ref, recipeId, caption: caption.trim() || undefined });
      if (recipeId) setCookStep(recipeId, null);
      nav.goBack();
    } catch (e) {
      notify(String((e as Error).message ?? e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Screen>
      <Body secondary>{t('logMeal.subtitle')}</Body>
      {photo ? (
        <Photo media={{ uri: photo.uri }} seed="new" style={{ width: '100%', aspectRatio: 4 / 3, borderRadius: radius.l }} />
      ) : (
        <Photo seed="empty" icon="camera" style={{ width: '100%', aspectRatio: 4 / 3 }} iconSize={48} />
      )}
      <Row>
        <Button label={t('logMeal.takePhoto')} icon="camera" onPress={() => takePhoto().then(ph => ph && setPhoto(ph))} style={{ flexGrow: 1 }} iconOnly />
        <Button label={t('logMeal.choosePhoto')} icon="image" variant="secondary" onPress={() => pickPhoto().then(ph => ph && setPhoto(ph))} style={{ flexGrow: 1 }} iconOnly />
      </Row>
      <Field label={t('logMeal.caption')} value={caption} onChangeText={setCaption} maxLength={140} />
      <View style={{ gap: spacing.s }}>
        <Caption>{t('logMeal.linkRecipe')}</Caption>
        <Row>
          <Chip label={t('logMeal.noRecipe')} selected={!recipeId} onPress={() => setRecipeId(undefined)} />
          {suggestions.map(id => {
            const r = lookup(id);
            return r ? <Chip key={id} label={r.title} icon="recipe" selected={recipeId === id} onPress={() => setRecipeId(id)} /> : null;
          })}
        </Row>
      </View>
      <Button label={t('logMeal.save')} icon="check" big onPress={save} disabled={!photo} loading={saving} iconOnly />
    </Screen>
  );
}
